import { PayloadAction } from "@reduxjs/toolkit";
import { put, SagaReturnType, select, takeLatest } from "redux-saga/effects";
import { applicationStateSelectors } from "store/slices/applicationState/selectors";
import { filtersSelectorStateActions } from "store/slices/filters/slice";
import { datesStateSelectors } from "store/slices/dates/selector";
import { EnvironmentState } from "store/slices/environment/slice";
import { environmentSelectors } from "store/slices/environment/selector";
import { setRooms } from "store/slices/rooms/slice";
import { ActiveFilters, FilterField } from "types/filters";

import { getProjectFilters } from "./filters.api";
import { filtersActions } from "./filters.actions";
import { roomsActions } from "../rooms/rooms.actions";

function* updateFiltersFields(action: PayloadAction<FilterField>) {
  yield put(filtersSelectorStateActions.setIsLoadingFilters(true));
  yield put(filtersSelectorStateActions.updateFiltersFields(action.payload));
  yield put(filtersSelectorStateActions.setIsLoadingFilters(false));
}

function* getProjectFiltersFields() {
  const projectId: string = yield select(applicationStateSelectors.projectID);
  if (sessionStorage.getItem("projectId")) {
    if (sessionStorage.getItem("projectId") !== projectId) {
      sessionStorage.removeItem("activeFilters");
      sessionStorage.setItem("projectId", projectId);
    }
  } else sessionStorage.setItem("projectId", projectId);

  yield put(filtersSelectorStateActions.setIsLoadingFilters(true));
  try {
    const environment: EnvironmentState = yield select(
      environmentSelectors.environment,
    );
    const filters: SagaReturnType<typeof getProjectFilters> =
      yield getProjectFilters(
        projectId,
        environment.REACT_APP_PROJECT_API_REST_URI,
      );
    const previousFilters = sessionStorage.getItem("activeFilters");
    if (previousFilters) {
      yield put(
        filtersSelectorStateActions.updateActiveFilters(
          JSON.parse(previousFilters),
        ),
      );
    }
    yield put(filtersSelectorStateActions.updateFiltersFields(filters));
    yield put(filtersActions.filterLoadingCompleted());
  } finally {
    yield put(filtersSelectorStateActions.setIsLoadingFilters(false));
  }
}

function* updateActiveFilters(action: PayloadAction<ActiveFilters>) {
  yield put(filtersSelectorStateActions.updateActiveFilters(action.payload));
}

function* resetActiveFilters() {
  sessionStorage.removeItem("activeFilters");
  yield put(filtersSelectorStateActions.updateActiveFilters({}));
  const startEndDates: {
    startDate: string;
    endDate: string;
  } = yield select(datesStateSelectors.startEndDates);
  yield put(
    roomsActions.getRoomsByProjectRequested({
      dates: startEndDates,
      filters: {},
    }),
  );
}

function* saveActiveFilters(action: PayloadAction<ActiveFilters>) {
  const newFilters = action.payload;

  Object.keys(newFilters).forEach((key) => {
    if (
      !newFilters[key]?.length ||
      newFilters[key] === null ||
      newFilters[key] === undefined
    ) {
      delete newFilters[key];
    }
  });

  sessionStorage.setItem("activeFilters", JSON.stringify(newFilters));
  const startEndDates: {
    startDate: string;
    endDate: string;
  } = yield select(datesStateSelectors.startEndDates);
  yield put(
    setRooms({
      data: [],
      paginationInfo: {
        __typename: "PaginationData",
        totalDocs: 0,
        totalPages: 0,
        prevPage: null,
        nextPage: null,
        hasNextPage: false,
        hasPrevPage: false,
      },
    }),
  );
  yield put(
    roomsActions.getRoomsByProjectRequested({
      dates: startEndDates,
      filters: newFilters,
      pagination: {
        page: 1,
        perPage: 10,
      },
    }),
  );
}

export default function* filtersSaga() {
  yield takeLatest(filtersActions.updateFiltersFields, updateFiltersFields);
  yield takeLatest(filtersActions.updateActiveFilters, updateActiveFilters);
  yield takeLatest(filtersActions.resetActiveFilters, resetActiveFilters);
  yield takeLatest(filtersActions.saveActiveFilters, saveActiveFilters);
  yield takeLatest(
    filtersActions.getProjectFiltersFields,
    getProjectFiltersFields,
  );
}
